package com.arena_web.pages

import androidx.compose.runtime.*
import com.arena_web.core.viewmodel.DashboardViewModel
import com.arena_web.core.viewmodel.rememberViewModel
import com.arena_web.di.LocalAppContainer
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.navigation.BusinessSidebarTab
import com.arena_web.ui.layouts.BusinessPageLayout
import com.arena_web.ui.layouts.LocalSelectedClubId
import com.arena_web.ui.sections.dashboard.*
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

@Page("/dashboard")
@Composable
fun DashboardPage() {
    val container = LocalAppContainer.current
    val clubAdminRepo = container.clubAdminRepository

    var activeSection by remember { mutableStateOf(DashboardSection.Analytics) }

    val viewModel = if (clubAdminRepo != null) rememberViewModel {
        DashboardViewModel(clubAdminRepo)
    } else null

    val state = viewModel?.uiState?.collectAsState()?.value

    BusinessPageLayout(
        activeTab = BusinessSidebarTab.Dashboard
    ) {
        if (viewModel == null || state == null) return@BusinessPageLayout

        val layoutClubId = LocalSelectedClubId.current
        LaunchedEffect(layoutClubId) {
            if (layoutClubId != null && layoutClubId != state.clubId) {
                viewModel.switchClub(layoutClubId)
            }
        }

        // Loading state
        if (state.isLoading && state.clubId == null) return@BusinessPageLayout

        Column(
            modifier = Modifier
                .fillMaxSize()
                .sPadding(24, 40)
                .sGap(20)
                .overflow(Overflow.Auto)
                .styleModifier {
                    property("scrollbar-width", "thin")
                    property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                }
        ) {
            // Sub-nav: Analytics / Schedule
            DashboardSubNav(
                activeSection = activeSection,
                onSectionSelected = { activeSection = it }
            )

            if (activeSection == DashboardSection.Analytics) {
                // Top row: Revenue (560) | Highlight (680) | Bookings (560)
                Div(attrs = {
                    style {
                        width(100.percent)
                        property("display", "grid")
                        property("grid-template-columns", "560fr 680fr 560fr")
                        property("gap", s(20))
                        property("height", s(425))
                        property("flex-shrink", "0")
                    }
                }) {
                    RevenueBlock(
                        chartData = state.chartData,
                        onRequestPeriod = { viewModel.requestChartData(it) }
                    )
                    // Center highlight image
                    Box(
                        modifier = Modifier
                            .sBorderRadius(22)
                            .styleModifier {
                                property("background",
                                    "linear-gradient(135deg, rgba(0, 97, 255, 0.12) 0%, rgba(60, 121, 241, 0.06) 100%)")
                                property("border", "1px solid rgba(41, 52, 138, 0.15)")
                                property("background-image", "url('/icons/image_football_team.png')")
                                property("background-size", "cover")
                                property("background-position", "center")
                            }
                    )
                    BookingsBlock(
                        chartData = state.chartData,
                        onRequestPeriod = { viewModel.requestChartData(it) }
                    )
                }

                // Bottom row: Status (596) | Stats (608) | Heatmap (596)
                Div(attrs = {
                    style {
                        width(100.percent)
                        property("display", "grid")
                        property("grid-template-columns", "596fr 608fr 596fr")
                        property("gap", s(20))
                        property("height", s(420))
                        property("flex-shrink", "0")
                    }
                }) {
                    BookingStatusBlock(
                        chartData = state.chartData,
                        onRequestPeriod = { viewModel.requestChartData(it) }
                    )
                    DashboardStatCardsGrid(
                        revenue = state.stats?.revenue,
                        bookingCount = state.stats?.bookingCount,
                        averageRating = state.club?.averageRating ?: state.stats?.averageRating,
                        totalRatingsCount = state.club?.totalRatingsCount
                    )
                    BookingHeatmapBlock(
                        chartData = state.chartData,
                        totalFields = state.fields.size,
                        onRequestPeriod = { viewModel.requestChartData(it) }
                    )
                }
            } else {
                DashboardScheduleSection(
                    calendar = state.calendar,
                    fields = state.fields,
                    weekDayLabels = viewModel.getWeekDayLabels(),
                    pendingCount = state.pendingBookings.size,
                    onNavigateWeek = { viewModel.navigateWeek(it) },
                    onGoToThisWeek = { viewModel.goToThisWeek() },
                    onApproveBooking = { viewModel.approveBooking(it) },
                    onRejectBooking = { viewModel.rejectBooking(it) },
                    onMarkPaid = { viewModel.markBookingPaid(it) },
                    onCompleteBooking = { viewModel.completeBooking(it) },
                    onStartBooking = { viewModel.startBooking(it) }
                )
            }
        }
    }
}
